import React, { useRef, useState } from 'react';
import { Upload, Link as LinkIcon, X, Loader2, FileText, Film, ImageIcon, Images, Music } from 'lucide-react';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import { uploadsService } from '@/services/uploads.service';
import { Input } from './Input';
import { MediaLibraryPicker } from './MediaLibraryPicker';
import { MediaLibraryModal } from './MediaLibraryModal';

type Mode = 'image' | 'video' | 'document' | 'audio';

interface Props {
  mode: Mode;
  value?: string;                                  // current URL
  onChange: (url: string, meta?: { name?: string; size?: number; mime?: string; fileId?: string }) => void;
  filename?: string;                                // for documents
  onFilenameChange?: (name: string) => void;
  folder?: string;
  className?: string;
}

const ACCEPT: Record<Mode, string> = {
  audio: 'audio/mpeg,audio/mp4,audio/aac,audio/amr,audio/ogg',
  image: 'image/jpeg,image/png,image/webp,image/gif',
  video: 'video/mp4,video/3gpp,video/quicktime',
  document: 'application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/plain',
};

const MODE_ICON: Record<Mode, React.ComponentType<{ size?: number; className?: string }>> = {
  image: ImageIcon,
  video: Film,
  document: FileText,
  audio: Music,
};

const MODE_LABEL: Record<Mode, string> = {
  image: 'image',
  video: 'video',
  document: 'document',
  audio: 'audio',
};

export const MediaUpload: React.FC<Props> = ({ mode, value, onChange, filename, onFilenameChange, folder, className }) => {
  const fileRef = useRef<HTMLInputElement>(null);
  const [tab, setTab] = useState<'upload' | 'url'>(value ? 'url' : 'upload');
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);
  const [libraryOpen, setLibraryOpen] = useState(false);
  const Icon = MODE_ICON[mode];
  const scopedLibrary = !!folder;
  const libraryEnabled = scopedLibrary || mode === 'image' || mode === 'video';
  const whatsapp = folder?.startsWith('whatsapp/');

  const upload = async (file: File) => {
    const maxMb = whatsapp ? mode === 'image' ? 5 : mode === 'document' ? 50 : 16 : 50;
    if (file.size > maxMb * 1024 * 1024) { toast.error(`Choose a file smaller than ${maxMb} MB.`); return; }
    if (whatsapp && mode === 'image' && !['image/jpeg', 'image/png'].includes(file.type)) { toast.error('WhatsApp images must be JPEG or PNG.'); return; }
    setBusy(true);
    setProgress(0);
    const tick = setInterval(() => setProgress((p) => Math.min(90, p + Math.random() * 10)), 280);
    try {
      const r = await uploadsService.imagekit(file, folder);
      setProgress(100);
      onChange(r.url, { name: r.name, size: r.size, mime: r.fileType, fileId: r.fileId });
      if (mode === 'document' && onFilenameChange) onFilenameChange(r.name);
      toast.success('Uploaded');
    } catch (e: any) {
      if (!e?.response) toast.error(e?.message ?? 'Upload failed');
    } finally {
      clearInterval(tick);
      setBusy(false);
      setTimeout(() => setProgress(0), 600);
    }
  };

  const onPick = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (f) upload(f);
    e.target.value = '';
  };

  const clear = () => {
    onChange('');
    if (mode === 'document' && onFilenameChange) onFilenameChange('');
  };

  return (
    <div className={clsx('rounded-xl border border-border bg-surface', className)}>
      {/* Tabs */}
      <div className="flex items-center gap-1 border-b border-border bg-surface-alt/60 p-1">
        <Tab active={tab === 'upload'} onClick={() => setTab('upload')} icon={<Upload size={12} />} label="Upload" />
        {libraryEnabled && (
          <Tab active={false} onClick={() => setLibraryOpen(true)} icon={<Images size={12} />} label="Library" />
        )}
        <Tab active={tab === 'url'} onClick={() => setTab('url')} icon={<LinkIcon size={12} />} label="Paste URL" />
      </div>

      {libraryEnabled && !scopedLibrary && (
        <MediaLibraryPicker
          open={libraryOpen}
          onClose={() => setLibraryOpen(false)}
          mediaType={mode as 'image' | 'video'}
          onPick={(asset) => {
            onChange(asset.url, { name: asset.filename, mime: undefined });
            setTab('url'); // surfaces the preview block for the chosen URL
          }}
        />
      )}

      {scopedLibrary && folder && (
        <MediaLibraryModal open={libraryOpen} onClose={() => setLibraryOpen(false)} folder={folder} type={mode} multiple={false}
          title={`Your ${MODE_LABEL[mode]} library`}
          onPick={([asset]) => {
            if (!asset) return;
            onChange(asset.url, { name: asset.name, size: asset.size, mime: asset.mime, fileId: asset.fileId });
            if (mode === 'document') onFilenameChange?.(asset.name);
            setTab('upload');
          }} />
      )}

      <div className="p-3.5">
        {tab === 'upload' ? (
          <>
            <input
              ref={fileRef}
              type="file"
              accept={whatsapp && mode === 'image' ? 'image/jpeg,image/png' : ACCEPT[mode]}
              onChange={onPick}
              className="hidden"
            />
            {value ? (
              <Preview mode={mode} url={value} filename={filename} onReplace={() => fileRef.current?.click()} onRemove={clear} />
            ) : (
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                disabled={busy}
                className="group flex w-full flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed border-border bg-surface-alt/40 px-6 py-8 text-center transition-colors hover:border-primary hover:bg-primary-soft/40 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {busy ? (
                  <>
                    <Loader2 size={22} className="animate-spin text-primary" />
                    <div className="text-xs font-medium text-text-secondary">Uploading… {Math.round(progress)}%</div>
                    <div className="h-1 w-40 overflow-hidden rounded-full bg-border">
                      <div className="h-full bg-gold-gradient transition-all" style={{ width: `${progress}%` }} />
                    </div>
                  </>
                ) : (
                  <>
                    <span className="flex h-11 w-11 items-center justify-center rounded-full bg-primary-soft text-primary group-hover:bg-gold-gradient group-hover:text-on-primary transition-colors">
                      <Upload size={18} />
                    </span>
                    <div className="text-sm font-semibold text-text-primary">Choose {MODE_LABEL[mode]} to upload</div>
                    <div className="text-2xs text-text-muted">
                      {mode === 'image' && (whatsapp ? 'JPG, PNG · up to 5 MB' : 'JPG, PNG, WEBP, GIF · up to 50 MB')}
                      {mode === 'video' && (whatsapp ? 'MP4, 3GP · up to 16 MB' : 'MP4, 3GP, MOV · up to 50 MB')}
                      {mode === 'audio' && 'MP3, M4A, AAC, OGG · up to 16 MB'}
                      {mode === 'document' && 'PDF, DOCX, XLSX, TXT · up to 50 MB'}
                    </div>
                  </>
                )}
              </button>
            )}
          </>
        ) : (
          <div className="space-y-2">
            <Input
              type="url"
              value={value ?? ''}
              onChange={(e) => onChange(e.target.value)}
              placeholder={`https://example.com/sample.${mode === 'image' ? 'png' : mode === 'video' ? 'mp4' : mode === 'audio' ? 'mp3' : 'pdf'}`}
              iconLeft={<LinkIcon size={14} />}
            />
            {mode === 'document' && (
              <Input
                value={filename ?? ''}
                onChange={(e) => onFilenameChange?.(e.target.value)}
                placeholder="Filename (e.g. invoice.pdf)"
                iconLeft={<Icon size={14} />}
              />
            )}
            {value && (
              <Preview mode={mode} url={value} filename={filename} onRemove={clear} />
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const Tab: React.FC<{ active: boolean; onClick: () => void; icon: React.ReactNode; label: string }> = ({ active, onClick, icon, label }) => (
  <button
    type="button"
    onClick={onClick}
    className={clsx(
      'inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium transition-colors',
      active ? 'bg-surface text-text-primary shadow-soft' : 'text-text-muted hover:bg-surface hover:text-text-primary',
    )}
  >
    {icon} {label}
  </button>
);

const Preview: React.FC<{ mode: Mode; url: string; filename?: string; onReplace?: () => void; onRemove: () => void }> = ({
  mode, url, filename, onReplace, onRemove,
}) => (
  <div className="overflow-hidden rounded-lg border border-border bg-surface-alt/60">
    {mode === 'image' && (
      <div className="relative">
        <img src={url} alt="" className="block max-h-48 w-full object-contain bg-checker" onError={(e) => { (e.target as HTMLImageElement).style.opacity = '0.3'; }} />
      </div>
    )}
    {mode === 'video' && (
      <video src={url} controls className="block max-h-56 w-full bg-black/80" />
    )}
    {mode === 'audio' && <audio src={url} controls className="w-full px-3 py-2" />}
    {mode === 'document' && (
      <div className="flex items-center gap-3 px-4 py-3.5">
        <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-info-soft text-info">
          <FileText size={18} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="truncate text-sm font-semibold text-text-primary">{filename || 'Document'}</div>
          <a href={url} target="_blank" rel="noreferrer" className="truncate text-xs text-text-muted hover:text-primary">
            {url}
          </a>
        </div>
      </div>
    )}
    <div className="flex items-center justify-between gap-2 border-t border-border bg-surface px-3 py-2">
      <div className="truncate text-2xs text-text-muted">{url}</div>
      <div className="flex shrink-0 gap-1">
        {onReplace && (
          <button type="button" onClick={onReplace} className="rounded-md px-2 py-1 text-xs font-medium text-text-secondary hover:bg-surface-alt hover:text-text-primary">
            Replace
          </button>
        )}
        <button type="button" onClick={onRemove} className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-text-muted hover:bg-danger-soft hover:text-danger">
          <X size={11} /> Remove
        </button>
      </div>
    </div>
  </div>
);
